import { useState, useEffect } from 'react';

/**
 * Initialize state with undefined width/height so server and client renders match
 * Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
 *
 */
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

/**
 * Check banner if there's any
 *
 * */
export const useBannerFlag = () => {
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    function checkBanner() {
      const flag = localStorage.getItem('hideBanner');
      setFlag(JSON.parse(flag));
    }

    window.addEventListener('storage', checkBanner);
    checkBanner();

    return () => window.removeEventListener('storage', checkBanner);
  }, []);

  return flag;
};

/**
 * Convert URLs from one location to another,
 * specifically changing URLs that point to 'https://alloy-assets.s3.amazonaws.com/'
 * to 'https://cdn.runalloy.com/'.
 *
 */
export const convertUrltoCdn = url => {
  if (url && url.includes('https://alloy-assets.s3.amazonaws.com/')) {
    return url.replace(
      'https://alloy-assets.s3.amazonaws.com/',
      'https://cdn.runalloy.com/'
    );
  }

  return url;
};

/**
 * Function to replace the domain in img src attributes
 * from 'https://alloy-assets.s3.amazonaws.com/' to 'https://cdn.runalloy.com/'.
 *
 */
export const formatHtmlContent = html => {
  // Define a regular expression to match img src attributes
  const imgSrcRegex =
    /<img [^>]*src=['"](https:\/\/alloy-assets\.s3\.amazonaws\.com\/[^'"]+)['"][^>]*>/g;

  // Replace the domain in img src attributes
  const modifiedHtmlContent = html.replace(imgSrcRegex, (match, src) => {
    const newSrc = src.replace(
      'https://alloy-assets.s3.amazonaws.com/',
      'https://cdn.runalloy.com/'
    );
    return match.replace(src, newSrc);
  });

  return modifiedHtmlContent;
};

/**
 * Takes an input array and shuffles its elements
 * randomly using the Fisher-Yates shuffle algorithm.
 *
 * This results in a new array where the elements
 * are in a random order. It's commonly used for tasks like randomizing
 * the order of items in a list or creating randomized game decks.
 *
 */
export const randomArrayShuffle = array => {
  let currentIndex = array.length,
    randomIndex;

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
};

/**
 * Retrieve the base URL.
 *
 */
export const getAppUrl = () => {
  let appUrl = 'http://localhost:8000';

  if (process.env.APP_URL) {
    appUrl = process.env.APP_URL;
  }

  return appUrl;
};

/**
 * Retrieve the base URL for API.
 *
 */
export const getApiUrl = () => {
  let apiUrl = 'http://localhost:4040';

  if (process.env.APP_URL) {
    apiUrl = process.env.API_URL;
  }

  return apiUrl;
};

/**
 * For creating scroll-based animations, progress indicators,
 * or other actions that depend on the user's scroll position within a webpage.
 *
 */
export const handleScroll = setPlaybookProgress => {
  return () => {
    const el = document.documentElement;
    const height = el.scrollHeight - el.clientHeight;
    const percent = (el.scrollTop / height) * 100;

    setPlaybookProgress(percent);
  };
};

/**
 * This function is designed to make a video player behave in a
 * specific way when the user is using the Safari browser.
 *
 * It hides the default controls, mutes the audio,
 * and starts the video playing automatically,
 * while also providing a fallback to hide the player
 * if there are any playback issues.
 *
 * This is useful when you want to ensure a consistent
 * video playback experience on Safari.
 *
 */
export const configurePlayerForSafari = ref => {
  const isSafari = () => {
    if (!navigator) {
      return false;
    }

    const ua = navigator.userAgent.toLowerCase();
    return ua.indexOf('safari') > -1 && ua.indexOf('chrome') < 0;
  };

  if (isSafari() && ref?.current) {
    const player = ref?.current?.children[0];

    if (player) {
      player.controls = false;
      player.playsinline = true;
      player.muted = true;
      player.setAttribute('muted', '');
      player.autoplay = true;

      setTimeout(() => {
        const promise = player.play();
        if (promise.then) {
          promise
            .then(() => {})
            .catch(e => {
              ref.current.style.display = 'none';
            });
        }
      }, 0);
    }
  }
};

/**
 * Scroll to section function designed to scroll the page to a specific section
 * with a given `id` while accounting for the position of a fixed navigation bar.
 *
 */
export const scrollToSection = id => {
  const element = document.getElementById(id);
  const screenSize = useWindowSize;

  if (element) {
    const topPos = element.getBoundingClientRect().top + window.pageYOffset;
    const navBarHeight = screenSize.width < 768 ? 92 : 122;

    setTimeout(() => {
      window.scrollTo({
        top: topPos - navBarHeight,
        behavior: 'smooth',
      });
    }, 200);
  }
};

/** Bold string on input */
export const boldStringOnInput = (str, query) => {
  const n = str.toUpperCase();
  const q = query.toUpperCase();
  const x = n.indexOf(q);

  if (!q || x === -1) {
    return str;
  }

  const l = q.length;

  return (
    str.substr(0, x) + '<b>' + str.substr(x, l) + '</b>' + str.substr(x + l)
  );
};

/** Update URL query */
export const updateURLQuery = query => {
  if (window?.history?.replaceState) {
    var newurl = window.location.origin + window.location.pathname + query;
    window.history.replaceState({ path: newurl }, '', newurl);
  }
};

/** Formatting utility function */
export const formatNumber = value => {
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
